(() => {
  'use strict';

  const init = () => {
    const dialog = document.getElementById('mb-store-notice');
    if (!dialog || dialog.dataset.mbNoticeInitialized) return;
    dialog.dataset.mbNoticeInitialized = 'true';
    const setPlaybackState = (state) => {
      if (dialog.dataset.mbNoticeState === state) return;
      dialog.dataset.mbNoticeState = state;
      document.dispatchEvent(new CustomEvent('mb:store-notice-state', {
        detail: { state, blocking: state !== 'dismissed' },
      }));
    };
    // A closed dialog is not necessarily dismissed: it may still be waiting
    // for the initial delay or for Shopify's privacy controls to finish.
    setPlaybackState('pending');
    if (typeof dialog.showModal !== 'function') {
      setPlaybackState('dismissed');
      return;
    }

    const storageKey = `martenbrown:store-notice:${dialog.dataset.mbNoticeVersion}`;
    try {
      if (window.sessionStorage.getItem(storageKey) === 'seen') {
        setPlaybackState('dismissed');
        return;
      }
    } catch (_error) {
      // The notice remains fully usable when the browser blocks session storage.
    }

    const blockerSelector = [
      '#shopify-pc__banner',
      '#shopify-pc__prefs',
      '#shopify-privacy-banner',
      '[data-shopify-privacy-banner]',
      '.fancybox-is-open',
      'dialog[open]:not(#mb-store-notice)',
      '[role="dialog"][aria-modal="true"]:not(#mb-store-notice)',
    ].join(',');
    let previousFocus = null;
    let dismissed = false;
    let timer = null;
    let pointerStartedOutside = false;
    let consentClosePending = false;

    const hasVisibleBlocker = () => {
      try {
        if (window.Shopify?.customerPrivacy?.shouldShowBanner?.() === true) return true;
      } catch (_error) {
        // The visibility check also works before Shopify's privacy API is ready.
      }
      return Array.from(document.querySelectorAll(blockerSelector)).some((element) => {
        if (dialog.contains(element) || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
        return element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== 'hidden';
      });
    };

    const scheduleAttempt = (delay = 250) => {
      if (dismissed || timer !== null) return;
      timer = window.setTimeout(() => {
        timer = null;
        attemptOpen();
      }, delay);
    };

    const observer = new MutationObserver(() => scheduleAttempt());

    const stopWatching = () => {
      observer.disconnect();
      window.clearTimeout(timer);
      timer = null;
      document.removeEventListener('visibilitychange', scheduleAfterVisibilityChange);
      document.removeEventListener('visitorConsentCollected', scheduleAfterConsent);
    };

    const rememberDismissal = () => {
      dismissed = true;
      stopWatching();
      try {
        window.sessionStorage.setItem(storageKey, 'seen');
      } catch (_error) {
        // Storage is optional; dismissal and navigation must always work.
      }
    };

    const attemptOpen = () => {
      if (dismissed || document.hidden || !dialog.isConnected || consentClosePending) return;
      if (hasVisibleBlocker()) {
        // A consent banner that arrives late must remain operable, too.
        setPlaybackState('pending');
        if (dialog.open) {
          consentClosePending = true;
          dialog.close('mb-wait-for-consent');
        }
        return;
      }
      if (dialog.open) return;

      if (previousFocus === null) previousFocus = document.activeElement;
      dialog.returnValue = '';
      dialog.showModal();
      setPlaybackState('open');
      dialog.scrollTop = 0;
      document.documentElement.classList.add('mb-store-notice-open');
      dialog.querySelector('.mb-store-notice__title')?.focus({ preventScroll: true });
    };

    const scheduleAfterVisibilityChange = () => {
      if (!document.hidden) scheduleAttempt();
    };
    const scheduleAfterConsent = () => scheduleAttempt();

    dialog.querySelectorAll('[data-mb-notice-close], [data-mb-notice-continue]').forEach((control) => {
      control.addEventListener('click', () => {
        // The primary link can navigate before the asynchronous close event.
        rememberDismissal();
        dialog.close();
      });
    });

    const isOutside = (event) => {
      const bounds = dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right
        || event.clientY < bounds.top || event.clientY > bounds.bottom;
    };

    dialog.addEventListener('pointerdown', (event) => {
      pointerStartedOutside = event.target === dialog && isOutside(event);
    });
    dialog.addEventListener('click', (event) => {
      if (pointerStartedOutside && event.target === dialog && isOutside(event)) dialog.close();
      pointerStartedOutside = false;
    });

    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('mb-store-notice-open');
      // returnValue is mutable when another opening begins; keep a separate
      // flag until the asynchronous native close event has been consumed.
      if (consentClosePending) {
        consentClosePending = false;
        setPlaybackState('pending');
        scheduleAttempt();
        return;
      }
      rememberDismissal();
      if (previousFocus && previousFocus.isConnected && previousFocus !== document.body && typeof previousFocus.focus === 'function') {
        previousFocus.focus({ preventScroll: true });
      }
      setPlaybackState('dismissed');
    });

    // Shopify privacy controls and any existing modal take priority over this notice.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'open'],
    });
    document.addEventListener('visibilitychange', scheduleAfterVisibilityChange);
    document.addEventListener('visitorConsentCollected', scheduleAfterConsent);
    scheduleAttempt(900);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
